import { Box, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ComposedParametersValue } from '../../../../../../redux/CUParameters/selectors';
import { ParameterProps } from './Parameter';
import { ParameterEditControl } from './ParameterEditControl/ParameterEditControl';
import {
  DialogModal,
  DialogModalProps,
} from '../../../../../../theme/components';
import { useShowErrorMessage } from '../../../../../../handlingErrors';
import {
  DEFAULT_DECIMALS,
  DEFAULT_UNIT_SENSOR,
} from '../../../../../../redux/controlUnit/api/util/constants';
import {
  TemperatureUnit,
  fahrenheitToCelsius,
} from '../../../../../../customized/fresh-watch/theme/utils/temperatureUtils';
import {
  getDecimals,
  getPreferredUnit,
  getValueWithPreferredUnitAndRounded,
} from '../../../../../../redux/controlUnit/api/util/sensor/formatValues';
import usePreferredTemperatureUnit from '../../../../../../redux/settings/hooks/usePreferredTemperatureUnit';
import _ from 'lodash';

export interface ParameterEditModalProps
  extends ParameterProps,
    Omit<DialogModalProps, 'onSave'> {
  onSave: (parameter: ComposedParametersValue, newValue: string) => void;
}
export function ParameterEditModal(props: ParameterEditModalProps) {
  const { parameter, onSave } = props;
  const showErrorMessage = useShowErrorMessage();
  const title = parameter.titleTranslation;
  const { data: preferredTemperatureUnit } = usePreferredTemperatureUnit();
  const temperatureUnit = getPreferredUnit(
    parameter.unitString ?? parameter.unit,
    preferredTemperatureUnit
  ) as TemperatureUnit;

  const resetValue = (value: number) => {
    /* Transforms back information to °C */
    if (temperatureUnit === '°F')
      value = _.round(
        fahrenheitToCelsius(value),
        parameter.decimals ?? DEFAULT_DECIMALS
      );

    return Number(value);
  };

  const decimals = getDecimals(parameter.decimals, preferredTemperatureUnit);

  /* Sets the temperature depending on users chose */
  const incomingFixedValue = getValueWithPreferredUnitAndRounded(
    parameter.value
      ? parseFloat(parameter.value)
      : _.ceil(parameter.min, decimals),
    {
      decimals: parameter.decimals,
      unitString: parameter.unitString ?? parameter.unit ?? DEFAULT_UNIT_SENSOR,
      maxVal: parameter.max,
      minVal: parameter.min,
      id: parameter.name,
      name: parameter.name,
      unit: parameter.unit,
    },
    preferredTemperatureUnit
  );

  const [temporalValue, setTemporalValue] = useState(
    incomingFixedValue.toString()
  );
  const [errorForm, setErrorForm] = useState<string | null | undefined>(
    undefined
  );
  useEffect(() => {
    if (props.open) setTemporalValue(incomingFixedValue.toString());
  }, [props.open]);
  const onClickSave = () => {
    /* Saves the value in °C */
    const result = resetValue(parseFloat(temporalValue));
    if (errorForm) return showErrorMessage(new Error(errorForm));
    else onSave(parameter, result.toString());
  };
  const onChange = (value: string, error?: string) => {
    setTemporalValue(value);
    setErrorForm(error);
  };
  return (
    <DialogModal {...props} onConfirm={onClickSave}>
      <Stack spacing={1} minWidth={300} px={2}>
        <Typography variant="h4">{title}</Typography>
        <Typography>{parameter.description}</Typography>
        <Box m={1}>
          <ParameterEditControl
            {...props}
            value={temporalValue}
            onChangeValue={onChange}
          />
        </Box>
      </Stack>
    </DialogModal>
  );
}
